/**
 * main.ts — orchestration for the Reflex Bandit demo.
 *
 * Two-loop structure (copied from mob):
 *   - rAF loop runs for the page lifetime (cheap; eases the agent animation
 *     and blits layers).
 *   - setInterval ticks the agent at a user-tunable Hz, decoupled from the
 *     render rate so the viewer can speed up / slow down.
 *
 * Lifecycle: 'idle' | 'running' | 'stopped'. When an episode finishes, the
 * next world from the current day's shape starts; every worldsPerDay
 * episodes the day boundary fires (reset_day on the bandit; the long-term
 * semantics survive). The drift detector is intentionally NOT reset at day
 * boundaries — it needs continuity to detect the drift AT the boundary.
 *
 * URL params: ?arm=ts|greedy|random &shape=circles|squares|blobs|spirals
 */

import './style.css';
import { START_R, START_C, DIRECTIONS, N_ACTIONS, N_FEATURES, DAY_SCHEDULE, type ArmMode, type ShapeName } from './constants';
import { SHAPES } from './shapes';
import type { Grid } from './shapes';
import { ReflexEnv } from './environment';
import { ReflexBandit, DEFAULT_BANDIT_HP } from './bandit';
import { ForestClient } from './forest_client';
import { Renderer, makeAgentAnim } from './render';
import { SidePanel, type PanelState, type PanelCallbacks } from './ui';
import { HelpModal } from './help';
import { makeRng } from './linalg';
import { DEFAULT_HARNESS_CONFIG, type HarnessEvent, type DiscreteArmResult, type GradualArmResult, type ConfusionRow } from './harness';

const DEFAULT_SPEED_HZ = 12;

type Phase = 'idle' | 'running' | 'stopped';

function parseArmParam(): ArmMode {
  const v = new URLSearchParams(window.location.search).get('arm');
  if (v === 'ts' || v === 'greedy' || v === 'random') return v;
  return 'ts';
}
function parseShapeParam(): ShapeName {
  const v = new URLSearchParams(window.location.search).get('shape') as ShapeName | null;
  if (v && ['circles', 'squares', 'blobs', 'spirals'].includes(v)) return v;
  return 'circles';
}

function main(): void {
  const app = document.getElementById('app')!;
  app.innerHTML = '';

  // Layout: canvas (left) + side panel (right).
  const lakeCanvas = document.createElement('canvas');
  lakeCanvas.className = 'lake';
  const lakeWrap = document.createElement('div');
  lakeWrap.className = 'lake-wrap';
  lakeWrap.appendChild(lakeCanvas);
  app.appendChild(lakeWrap);

  const panelSlot = document.createElement('div');
  panelSlot.className = 'panel-slot';
  app.appendChild(panelSlot);

  // ── Tunable state (closures; mutated by callbacks) ──────────────────
  let arm: ArmMode = parseArmParam();
  let shape: ShapeName = parseShapeParam();
  let worldsPerDay = 8;
  let speedHz = DEFAULT_SPEED_HZ;
  let revealAll = false;
  let showWindow = true;
  let phase: Phase = 'idle';
  let tickIntervalId: number | null = null;
  let runStartMs = 0;

  // Hyperparams (mirrored into the bandit; live-tunable).
  const hp = { ...DEFAULT_BANDIT_HP, mode: arm, seed: 0 };

  // ── Core objects ────────────────────────────────────────────────────
  const worldRng = makeRng(12345);
  let world: Grid = SHAPES[shape](worldRng);
  let env = new ReflexEnv(world);
  env.reset();
  const agent = new ReflexBandit(hp);
  const forestClient = new ForestClient(agent);
  forestClient.install();
  const agentAnim = makeAgentAnim(START_R, START_C);
  const renderer = new Renderer(lakeCanvas);

  // Run counters.
  let worldInDay = 0;
  let dayIdx = 0;
  let episodeGold = 0;
  let episodeSteps = 0;
  let episodeReturn = 0;
  let totalGold = 0;
  let totalSteps = 0;
  let lastSelect: { mean: Float64Array; std: Float64Array; chosenArm: number; reason: 'cold' | 'drift' | 'model' } = {
    mean: new Float64Array(N_ACTIONS),
    std: new Float64Array(N_ACTIONS),
    chosenArm: 0,
    reason: 'cold',
  };

  const featureBuf = new Float64Array(N_ACTIONS * N_FEATURES);
  // Per-action validity mask (in-bounds + not a discovered stone). Computed
  // each tick from perceived geometry and passed to agent.select — keeps the
  // bandit from repeatedly picking a known wall / OOB edge, which is the
  // deterministic deadlock that presents as "stuck turning in place".
  const validMask = new Uint8Array(N_ACTIONS);
  // Sticky-pose escape: how many consecutive ticks the pose failed to change.
  // If it crosses STICKY_LIMIT we override with a uniform-random valid action
  // — the mask can't see the case where the agent is surrounded by
  // UNDISCOVERED stones (those bump as "explore" and don't update the mask
  // until the next scan), so the escape is the safety net.
  const STICKY_LIMIT = 3;
  let stickyCount = 0;

  // ── Panel ───────────────────────────────────────────────────────────
  const cb: PanelCallbacks = {
    onStart: () => startRun(),
    onStop: () => stopRun(),
    onReset: () => resetRun(),
    onSpeed: (hz) => { speedHz = hz; if (phase === 'running') restartTickTimer(); },
    onArm: (mode) => {
      arm = mode;
      hp.mode = mode;
      agent.hp.mode = mode;
      syncUrlParam('arm', mode);
    },
    onShape: (s) => {
      shape = s;
      syncUrlParam('shape', s);
      // Regenerate the current world with the new shape (only when idle).
      if (phase === 'idle') {
        world = SHAPES[s](worldRng);
        env = new ReflexEnv(world);
        env.reset();
        agentAnim.r = START_R; agentAnim.c = START_C;
        agentAnim.sr = START_R; agentAnim.sc = START_C;
      }
    },
    onWorldsPerDay: (n) => { worldsPerDay = n; },
    onNTrees: (n) => { hp.nTrees = n; agent.hp.nTrees = n; },
    onMinSamplesLeaf: (n) => { hp.minSamplesLeaf = n; agent.hp.minSamplesLeaf = n; },
    onColdStartK: (k) => { hp.coldStartK = k; agent.hp.coldStartK = k; },
    onDriftLam: (lam) => { hp.drift.lam = lam; agent.hp.drift.lam = lam; agent.drift.hp.lam = lam; },
    onDriftLDrift: (l) => { hp.drift.lDrift = l; agent.hp.drift.lDrift = l; agent.drift.hp.lDrift = l; },
    onRefitEvery: (n) => { hp.refitEvery = n; agent.hp.refitEvery = n; },
    onRevealAll: (b) => { revealAll = b; },
    onShowWindow: (b) => { showWindow = b; },
    onRunEvaluation: () => runEvaluation(),
  };

  const panel = new SidePanel(cb, {
    arm, shape, worldsPerDay,
    nTrees: hp.nTrees, minSamplesLeaf: hp.minSamplesLeaf,
    coldStartK: hp.coldStartK, driftLam: hp.drift.lam,
    driftLDrift: hp.drift.lDrift, refitEvery: hp.refitEvery,
    speedHz, revealAll, showWindow,
  });
  panelSlot.appendChild(panel.el);

  // ── Help button + footer ────────────────────────────────────────────
  const helpBtn = document.createElement('button');
  helpBtn.className = 'help-btn';
  helpBtn.textContent = '?';
  document.body.appendChild(helpBtn);
  let helpModal: HelpModal | null = null;
  helpBtn.addEventListener('click', () => {
    if (helpModal) return;
    helpModal = new HelpModal(() => { helpModal?.remove(); helpModal = null; });
    helpModal.mount(document.body);
  });

  const footer = document.createElement('div');
  footer.className = 'app-footer';
  document.body.appendChild(footer);

  // ── Render loop ─────────────────────────────────────────────────────
  const ro = new ResizeObserver(() => renderer.resize());
  ro.observe(lakeCanvas);

  requestAnimationFrame(function frame(t) {
    agentAnim.r = env.r;
    agentAnim.c = env.c;
    renderer.frame(t, {
      env,
      agent: agentAnim,
      drift: agent.drift.status(),
      revealAll,
      showWindow,
    });
    requestAnimationFrame(frame);
  });

  // ── Tick timer ──────────────────────────────────────────────────────
  function restartTickTimer(): void {
    if (tickIntervalId !== null) window.clearInterval(tickIntervalId);
    const periodMs = 1000 / Math.max(1, speedHz);
    tickIntervalId = window.setInterval(tick, periodMs);
  }

  function tick(): void {
    if (phase !== 'running') return;
    if (env.done) {
      startNextWorld();
      return;
    }
    env.candidateFeatures(featureBuf);
    env.candidateValid(validMask);
    let sel = agent.select(featureBuf, validMask);
    // Sticky-pose escape: if the last few ticks didn't move us (the agent
    // managed to pick only blocked actions despite the mask — the rare
    // undiscovered-stone surround), override with a random valid action.
    // We don't interfere with the bandit's learn signal: the `observe()`
    // below still uses the chosen action's feature row and the env's true
    // reward, so the model learns from what actually happened.
    if (stickyCount >= STICKY_LIMIT) {
      const validIdx: number[] = [];
      for (let a = 0; a < N_ACTIONS; a++) if (validMask[a] !== 0) validIdx.push(a);
      if (validIdx.length > 0) {
        const escapeA = validIdx[Math.floor(Math.random() * validIdx.length)];
        sel = { ...sel, action: escapeA, reason: sel.reason };
      }
    }
    const xTaken = featureBuf.slice(sel.action * N_FEATURES, (sel.action + 1) * N_FEATURES);
    const preR = env.r;
    const preC = env.c;
    const { reward, done } = env.step(sel.action);
    agent.observe(xTaken, sel.action, reward);

    // Track pose changes for the sticky-pose escape.
    if (env.r !== preR || env.c !== preC) {
      stickyCount = 0;
    } else {
      stickyCount++;
    }

    // Animation cues.
    const [dr, dc] = DIRECTIONS[sel.action];
    if (dr !== 0 || dc !== 0) agentAnim.targetHeading = Math.atan2(dr, dc);
    if (reward === 1) { agentAnim.grabFlash = 1.0; episodeGold++; totalGold++; }
    else if (reward === -0.5) agentAnim.bumpFlash = 1.0;
    episodeSteps++; totalSteps++;
    episodeReturn += reward;
    lastSelect = { mean: sel.mean, std: sel.std, chosenArm: sel.action, reason: sel.reason };
    if (done) {
      // Episode boundary fires next tick.
    }
    pushPanelUpdate();
  }

  function startNextWorld(): void {
    worldInDay++;
    if (worldInDay >= worldsPerDay) {
      // Day boundary: reset the day buffer, advance the day index. The drift
      // detector is intentionally NOT reset.
      agent.resetDay();
      dayIdx = (dayIdx + 1) % DAY_SCHEDULE.length;
      worldInDay = 0;
      // Use the schedule's shape at this day index, ignoring the user's shape
      // selector while a run is in progress (the schedule is the evaluation).
      const nextShape = DAY_SCHEDULE[dayIdx];
      world = SHAPES[nextShape](makeRng(Date.now() & 0xffffffff));
    } else {
      world = SHAPES[DAY_SCHEDULE[dayIdx]](makeRng(Date.now() & 0xffffffff));
    }
    env = new ReflexEnv(world);
    env.reset();
    episodeGold = 0;
    episodeSteps = 0;
    episodeReturn = 0;
    stickyCount = 0;
    agentAnim.r = START_R; agentAnim.c = START_C;
    agentAnim.sr = START_R; agentAnim.sc = START_C;
  }

  function pushPanelUpdate(): void {
    const ps: PanelState = {
      running: phase === 'running',
      elapsedMs: phase === 'running' ? performance.now() - runStartMs : 0,
      speedHz,
      episode: dayIdx * worldsPerDay + worldInDay,
      worldInDay,
      dayIdx,
      dayShape: DAY_SCHEDULE[dayIdx],
      worldsPerDay,
      episodeGold,
      episodeGoldTotal: env.totalGold,
      episodeSteps,
      episodeReturn,
      armMean: lastSelect.mean,
      armStd: lastSelect.std,
      chosenArm: lastSelect.chosenArm,
      reason: lastSelect.reason,
      driftStatus: agent.drift.status(),
      driftExploreRemaining: agent.drift.exploreRemaining,
      nDrift: agent.drift.nDrift,
      nWarning: agent.drift.nWarning,
      dayBufferSize: agent.dayBufferSize,
      dayBufferCap: agent.hp.windowPerDay,
      longBufferSize: agent.longBufferSize,
      longBufferCap: agent.hp.longCap,
      nObserved: agent.nObserved,
      forestFit: agent.forest.isFit,
      fitsRequested: forestClient.fitsRequested,
      fitsCompleted: forestClient.fitsCompleted,
      lastFitMs: forestClient.lastFitMs,
      totalGold,
      totalSteps,
    };
    panel.update(ps);
  }

  function startRun(): void {
    if (phase === 'running') return;
    // Fresh agent + fresh world.
    agent.resetRun();
    agent.hp.mode = arm;
    dayIdx = 0;
    worldInDay = 0;
    episodeGold = 0; episodeSteps = 0; episodeReturn = 0;
    totalGold = 0; totalSteps = 0;
    world = SHAPES[DAY_SCHEDULE[0]](makeRng(Date.now() & 0xffffffff));
    env = new ReflexEnv(world);
    env.reset();
    agentAnim.r = START_R; agentAnim.c = START_C;
    agentAnim.sr = START_R; agentAnim.sc = START_C;
    stickyCount = 0;
    phase = 'running';
    runStartMs = performance.now();
    restartTickTimer();
    pushPanelUpdate();
  }

  function stopRun(): void {
    phase = 'stopped';
    if (tickIntervalId !== null) { window.clearInterval(tickIntervalId); tickIntervalId = null; }
    pushPanelUpdate();
  }

  function resetRun(): void {
    stopRun();
    phase = 'idle';
    agent.resetRun();
    dayIdx = 0; worldInDay = 0;
    episodeGold = 0; episodeSteps = 0; episodeReturn = 0;
    totalGold = 0; totalSteps = 0;
    world = SHAPES[shape](worldRng);
    env = new ReflexEnv(world);
    env.reset();
    agentAnim.r = START_R; agentAnim.c = START_C;
    agentAnim.sr = START_R; agentAnim.sc = START_C;
    stickyCount = 0;
    lastSelect = { mean: new Float64Array(N_ACTIONS), std: new Float64Array(N_ACTIONS), chosenArm: 0, reason: 'cold' };
    pushPanelUpdate();
  }

  function syncUrlParam(key: string, value: string): void {
    const u = new URL(window.location.href);
    u.searchParams.set(key, value);
    window.history.replaceState({}, '', u.toString());
  }

  // ── Harness (Run evaluation button) ─────────────────────────────────
  // The harness runs in a fresh worker so it doesn't compete with the live
  // forest-fit worker. We bundle harness.ts as a worker entry.
  let evalWorker: Worker | null = null;
  function runEvaluation(): void {
    if (evalWorker) {
      evalWorker.terminate();
      evalWorker = null;
    }
    panel.setEvalOutput('', 'starting…', true);
    const btn = panel.el.querySelector<HTMLButtonElement>('#btn-eval')!;
    btn.disabled = true;
    btn.textContent = 'Running…';
    evalWorker = new Worker(new URL('./harness_worker.ts', import.meta.url), { type: 'module' });
    evalWorker.onmessage = (e: MessageEvent<HarnessEvent>) => {
      const evt = e.data;
      if (evt.kind === 'progress') {
        panel.setEvalOutput(
          '',
          `${evt.schedule} · ${evt.arm} · seed ${evt.worldIdx}/${evt.nWorlds}…`,
          true,
        );
      } else if (evt.kind === 'done') {
        const html = renderEvalResults(evt.discrete, evt.gradual, evt.confusion);
        panel.setEvalOutput(html, 'complete', true);
        btn.disabled = false;
        btn.textContent = 'Run evaluation';
        evalWorker?.terminate();
        evalWorker = null;
      } else if (evt.kind === 'error') {
        panel.setEvalOutput(`<p style="color:var(--bad)">error: ${evt.message}</p>`, 'failed', true);
        btn.disabled = false;
        btn.textContent = 'Run evaluation';
        evalWorker?.terminate();
        evalWorker = null;
      }
    };
    evalWorker.postMessage({ kind: 'run', config: DEFAULT_HARNESS_CONFIG });
  }

  // ── Boot ────────────────────────────────────────────────────────────
  // Initial panel paint (idle state).
  resetRun();
}

/** Render the harness results as HTML tables (gold-per-day, within-day
 *  speedup, drift counts, confusion matrix). */
function renderEvalResults(
  discrete: DiscreteArmResult[],
  gradual: GradualArmResult[],
  confusion: ConfusionRow[],
): string {
  // Gold per day, averaged across worlds-in-day, per arm.
  const arms: ArmMode[] = ['ts', 'greedy', 'random'];
  const dayShape = ['circles', 'squares', 'circles', 'blobs'];
  let html = '<h3 style="margin:8px 0 4px;font-size:12px;text-transform:uppercase;letter-spacing:0.06em;color:var(--text-dim)">Gold per day (averaged across worlds)</h3>';
  html += '<table class="results-table"><thead><tr><th>day</th><th>shape</th>';
  for (const a of arms) html += `<th>${a}</th>`;
  html += '</tr></thead><tbody>';
  for (let d = 0; d < 4; d++) {
    html += `<tr><td>${d}</td><td>${dayShape[d]}</td>`;
    for (const a of arms) {
      const armRes = discrete.find((r) => r.arm === a);
      const dayRows = armRes?.results.filter((r) => r.dayIdx === d) || [];
      const mean = dayRows.length > 0 ? dayRows.reduce((s, r) => s + r.gold, 0) / dayRows.length : 0;
      html += `<td class="${a === 'ts' ? 'ts' : ''}">${mean.toFixed(1)}</td>`;
    }
    html += '</tr>';
  }
  html += '</tbody></table>';

  // H1: Day-0 ratio.
  const tsD0 = discrete.find((r) => r.arm === 'ts')?.results.filter((r) => r.dayIdx === 0).reduce((s, r) => s + r.gold, 0) || 0;
  const raD0 = discrete.find((r) => r.arm === 'random')?.results.filter((r) => r.dayIdx === 0).reduce((s, r) => s + r.gold, 0) || 0;
  const tsCount = discrete.find((r) => r.arm === 'ts')?.results.filter((r) => r.dayIdx === 0).length || 1;
  const raCount = discrete.find((r) => r.arm === 'random')?.results.filter((r) => r.dayIdx === 0).length || 1;
  const tsMean = tsD0 / tsCount;
  const raMean = raD0 / raCount;
  html += `<p class="hint">H1 (Day-0 reflex): TS = ${tsMean.toFixed(1)}, random = ${raMean.toFixed(1)} → <strong>${(tsMean / Math.max(0.01, raMean)).toFixed(2)}×</strong></p>`;

  // Drift events per arm on the gradual schedule.
  html += '<h3 style="margin:12px 0 4px;font-size:12px;text-transform:uppercase;letter-spacing:0.06em;color:var(--text-dim)">Drift events on gradual schedule</h3>';
  html += '<table class="results-table"><thead><tr><th>arm</th><th>warnings</th><th>drifts</th></tr></thead><tbody>';
  for (const a of arms) {
    const g = gradual.find((r) => r.arm === a);
    const warns = g?.driftEvents.filter((e) => e.status === 'warning').length || 0;
    const drifts = g?.driftEvents.filter((e) => e.status === 'drift').length || 0;
    html += `<tr><td>${a}</td><td>${warns}</td><td>${drifts}</td></tr>`;
  }
  html += '</tbody></table>';

  // Confusion matrix for the TS arm (early/mid/late). confusion is a flat
  // array of {phase, chosenTok, count}; group by phase for the table.
  if (confusion.length > 0) {
    const phases: Array<'early' | 'mid' | 'late'> = ['early', 'mid', 'late'];
    html += '<h3 style="margin:12px 0 4px;font-size:12px;text-transform:uppercase;letter-spacing:0.06em;color:var(--text-dim)">Reflex confusion (TS arm)</h3>';
    html += '<table class="results-table"><thead><tr><th>phase</th><th>OOB/stone</th><th>free</th><th>gold</th></tr></thead><tbody>';
    for (const phase of phases) {
      const rows = confusion.filter((r) => r.phase === phase);
      const oob = (rows.find((r) => r.chosenTok === -1)?.count || 0) + (rows.find((r) => r.chosenTok === 2)?.count || 0);
      const free = rows.find((r) => r.chosenTok === 0)?.count || 0;
      const gold = rows.find((r) => r.chosenTok === 1)?.count || 0;
      const total = Math.max(1, oob + free + gold);
      html += `<tr><td>${phase}</td><td>${((oob / total) * 100).toFixed(0)}%</td><td>${((free / total) * 100).toFixed(0)}%</td><td>${((gold / total) * 100).toFixed(0)}%</td></tr>`;
    }
    html += '</tbody></table>';
  }

  return html;
}

main();
